import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import ContractMetrics from './components/ContractMetrics';
import LicenseLimitBanner from '../../components/license/LicenseLimitBanner';
import ContractFilters from './components/ContractFilters';
import ContractTable from './components/ContractTable';
import ContractCardMobile from './components/ContractCardMobile';
import TemplateDocumentTable from './components/TemplateDocumentTable';
import TemplateDocumentCardMobile from './components/TemplateDocumentCardMobile';
import EmptyState from './components/EmptyState';
import Toast from '../../components/ui/Toast';

import contractService from '../../services/contractService';
import {
  deleteUploadedSignatureDocument,
  downloadUploadedSignaturePdf,
  listUploadedSignatureDocuments,
  listUploadedSignatureDocumentsPage,
} from '../../services/uploadedDocumentsService';
import { duplicateDocument } from '../../services/manualDocumentService';
import { cancelSignatureJob } from '../../services/signatureService';
import userService from '../../services/userService';
import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import DownloadDocumentOverlay from './components/DownloadDocumentOverlay';
import Button from '../../components/ui/Button';
import ContractDeleteDialog from './components/ContractDeleteDialog';
import SignatureCancelFeedbackDialog from './components/SignatureCancelFeedbackDialog';
import SignatureCancelConfirmDialog from './components/SignatureCancelConfirmDialog';
import SignatureStatusModal from './components/SignatureStatusModal';
import GeneratePdfConfirmDialog from './components/GeneratePdfConfirmDialog';
import SignatureSendChecklistModal from './components/SignatureSendChecklistModal';
import DocumentListSkeleton from './components/DocumentListSkeleton';
import DocumentActivityStrip from './components/DocumentActivityStrip';
import AdvancedDocumentSearchModal from '../../components/documents/AdvancedDocumentSearchModal';
import { DOCUMENT_SEARCH_MAX_RESULTS } from '../../utils/documentAdvancedSearch';
import { buildStoredDocumentContractPayload } from '../../utils/buildStoredDocumentContractPayload';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import { trackDocumentJob } from '../../utils/documentJobTracker';
import { pollDocumentJobStatus } from '../../utils/pollDocumentJobStatus';

const SignatureFieldEditorModal = lazy(
  () => import('./components/signature-field-editor/SignatureFieldEditorModal')
);
const UploadedDocumentPdfViewerModal = lazy(
  () => import('../uploaded-documents/components/UploadedDocumentPdfViewerModal')
);

function parseIsManualFlag(doc) {
  if (!doc || typeof doc !== 'object') return false;
  const v = doc.is_manual ?? doc.isManual;
  if (v === true || v === 1) return true;
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    return t === 'true' || t === '1' || t === 'yes';
  }
  return false;
}

function parseIsManualSignature(doc) {
  if (!doc || typeof doc !== 'object') return false;
  const v = doc.is_manual_signature ?? doc.isManualSignature;
  if (v === true || v === 1) return true;
  if (typeof v === 'string') {
    const t = v.trim().toLowerCase();
    return t === 'true' || t === '1' || t === 'yes';
  }
  return false;
}

function parseFillingType(doc) {
  const v = doc?.fillingType ?? doc?.filling_type;
  if (!v) return 'standard';
  return String(v).trim().toLowerCase();
}

function isCustomTemplateDocument(doc) {
  return parseFillingType(doc) === 'custom_template';
}

/** Align API casing/aliases so dashboard filters match (e.g. SIGNED vs signed). */
function normalizeDocumentStatus(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  const u = s.toUpperCase();
  if (u === 'SIGNED' || u === 'SIGN' || u === 'ASSINADO') return 'SIGNED';
  if (u === 'PENDING_SIGNATURE' || u === 'AWAITING_SIGNATURE') {
    return 'PENDING_SIGNATURE';
  }
  return u;
}

/** Match template keys across kebab/snake/case variants for type filter. */
function normalizeTemplateKey(raw) {
  if (raw == null || raw === '') return '';
  return String(raw).trim().replace(/-/g, '_').toLowerCase();
}

/** Mirrors ContractTableRow / ContractCardMobile `canEdit` for dashboard hints. */
function contractCanReceiveDashboardEdit(contract) {
  if (!contract || contract.is_manual_signature) return false;
  const isManual = Boolean(contract.is_manual);
  const isPendingSignature = contract.status === 'PENDING_SIGNATURE';
  const isSigned = contract.status === 'SIGNED';
  const canEditTemplate =
    contract.status === 'DRAFT' ||
    contract.status === 'PDF_GENERATED' ||
    contract.status === 'MANUAL';
  return isManual
    ? Boolean(contract.job_id) && !isSigned && !isPendingSignature
    : canEditTemplate;
}

const mapDocumentToContract = (doc) => ({
  id: doc?.s3_key,
  name: doc?.title || doc?.name || doc?.document_title || 'Sem título',
  type: normalizeTemplateKey(doc?.template_key ?? doc?.templateKey),
  status: normalizeDocumentStatus(doc?.status),
  job_id: doc?.job_id,
  createdAt: doc?.created_at,
  signatureDate: doc?.signature_date ?? null,
  s3Key: doc?.s3_key,
  is_manual: parseIsManualFlag(doc),
  fillingType: parseFillingType(doc),
  template_id: doc?.template_id ?? doc?.templateId ?? null,
  is_custom_template: isCustomTemplateDocument(doc),
  is_manual_signature: parseIsManualSignature(doc),
});

const mapUploadedDocumentToContract = (doc) => ({
  id: doc?.s3_key || doc?.job_id,
  name: doc?.title || 'Sem título',
  type: 'uploaded_signature',
  status: normalizeDocumentStatus(doc?.status),
  job_id: doc?.job_id,
  createdAt: doc?.created_at,
  signatureDate: doc?.signature_date ?? null,
  s3Key: doc?.s3_key,
  is_manual: false,
  is_manual_signature: true,
  fillingType: 'standard',
  template_id: null,
  is_custom_template: false,
  file_size_bytes:
    doc?.file_size_bytes != null
      ? Number(doc.file_size_bytes)
      : doc?.fileSizeBytes != null
        ? Number(doc.fileSizeBytes)
        : null,
});

const mapSearchItemToContract = (doc) =>
  parseIsManualSignature(doc) || doc?.is_manual_signature
    ? mapUploadedDocumentToContract(doc)
    : mapDocumentToContract(doc);

const LIST_PAGE_SIZE = 20;

function mergeContractsByCreatedAt(...groups) {
  const byJobId = new Map();
  for (const group of groups) {
    for (const doc of group) {
      if (doc?.job_id == null) continue;
      byJobId.set(String(doc.job_id), doc);
    }
  }
  return [...byJobId.values()].sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return db - da;
  });
}

/** Sort key matches ContractTable header ids. */
function compareContractsForSort(a, b, sortKey, sortDir) {
  const mul = sortDir === 'asc' ? 1 : -1;

  const cmpNullSignature = () => {
    const hasA = a.signatureDate != null && a.signatureDate !== '';
    const hasB = b.signatureDate != null && b.signatureDate !== '';
    if (!hasA && !hasB) return 0;
    if (!hasA) return 1;
    if (!hasB) return -1;
    const da = new Date(a.signatureDate).getTime();
    const db = new Date(b.signatureDate).getTime();
    return mul * (da - db);
  };

  switch (sortKey) {
    case 'name': {
      const va = (a.name || '').toLowerCase();
      const vb = (b.name || '').toLowerCase();
      return mul * va.localeCompare(vb, 'pt-BR');
    }
    case 'createdAt': {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return mul * (da - db);
    }
    case 'status': {
      const va = a.status || '';
      const vb = b.status || '';
      return mul * va.localeCompare(vb, 'pt-BR');
    }
    case 'signatureDate':
      return cmpNullSignature();
    default:
      return 0;
  }
}

const ContractDashboard = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all',
  });

  /** Todos = all; Modelos (Sistema) = padrão; Manuais = is_manual; Meus Modelos = custom_template; Uploads = PDF enviado */
  const [listTab, setListTab] = useState('todos');
  const isTemplatesTab = listTab === 'templates';
  const isUploadsTab = listTab === 'uploads';
  const isAllDocumentsTab = listTab === 'todos';

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [visibleCount, setVisibleCount] = useState(LIST_PAGE_SIZE);

  const [contracts, setContracts] = useState([]);
  const [listMode, setListMode] = useState('browse');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [isAdvancedSearching, setIsAdvancedSearching] = useState(false);

  const [generatedLastKey, setGeneratedLastKey] = useState(null);
  const [uploadsLastKey, setUploadsLastKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [signatureChecklistOpen, setSignatureChecklistOpen] = useState(false);
  const [signatureChecklistContract, setSignatureChecklistContract] =
    useState(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [generatingPdfJobId, setGeneratingPdfJobId] = useState(null);
  const [isPdfProcessing, setIsPdfProcessing] = useState(false);
  const [pdfConfirmContract, setPdfConfirmContract] = useState(null);

  const [isCancelingSignature, setIsCancelingSignature] = useState(false);
  const [signatureCancelFeedback, setSignatureCancelFeedback] = useState(null);
  const [signatureCancelConfirmOpen, setSignatureCancelConfirmOpen] =
    useState(false);
  const [pendingCancelSignatureContract, setPendingCancelSignatureContract] =
    useState(null);
  const [signatureStatusOpen, setSignatureStatusOpen] = useState(false);
  const [signatureStatusDocument, setSignatureStatusDocument] = useState(null);

  const [signatureEditorOpen, setSignatureEditorOpen] = useState(false);
  const [signatureEditorContract, setSignatureEditorContract] = useState(null);

  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerContract, setPdfViewerContract] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = (type, message, duration = 4000) => {
    const fallback =
      type === 'error'
        ? 'Ocorreu um erro. Tente novamente.'
        : '';
    const text = formatApiErrorMessage(message, fallback);
    setToast({ type, message: text });
    setTimeout(() => setToast(null), duration);
  };

  useEffect(() => {
    loadUserMetrics();
    loadDocuments();
  }, []);

  /* =======================
     USER METRICS
  ======================= */

  const loadUserMetrics = async () => {
    try {
      const result = await userService.getUserInfo();
      if (!result?.success || !result?.data) return;

      setUser(result.data);
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  /* =======================
     DOCUMENT LISTING
  ======================= */

  const activeContracts = listMode === 'search' ? searchResults : contracts;

  const filteredContracts = useMemo(() => {
    let result = [...activeContracts];

    if (listMode === 'browse') {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        result = result.filter((doc) =>
          doc.name?.toLowerCase().includes(search)
        );
      }

      if (filters.status !== 'all') {
        const want = normalizeDocumentStatus(filters.status);
        result = result.filter((doc) => doc.status === want);
      }

      if (filters.type !== 'all') {
        if (filters.type === 'MANUAL') {
          result = result.filter((doc) => doc.is_manual === true);
        } else if (filters.type === 'uploaded_signature') {
          result = result.filter((doc) => doc.is_manual_signature === true);
        } else {
          const want = normalizeTemplateKey(filters.type);
          result = result.filter((doc) => doc.type === want);
        }
      }
    }

    if (listTab === 'modelos') {
      result = result.filter(
        (doc) =>
          !doc.is_manual_signature &&
          !doc.is_manual &&
          doc.fillingType !== 'custom_template'
      );
    } else if (listTab === 'manuais') {
      result = result.filter((doc) => doc.is_manual === true);
    } else if (listTab === 'templates') {
      result = result.filter((doc) => doc.fillingType === 'custom_template');
    } else if (listTab === 'uploads') {
      result = result.filter((doc) => doc.is_manual_signature === true);
    }

    return result;
  }, [
    activeContracts,
    listMode,
    filters.search,
    filters.status,
    filters.type,
    listTab,
  ]);

  const sortedContracts = useMemo(() => {
    if (!sortKey) return filteredContracts;
    return [...filteredContracts].sort((a, b) =>
      compareContractsForSort(a, b, sortKey, sortDir)
    );
  }, [filteredContracts, sortKey, sortDir]);

  const visibleContracts = useMemo(() => {
    if (listMode === 'search') {
      return sortedContracts;
    }
    return sortedContracts.slice(0, visibleCount);
  }, [sortedContracts, visibleCount, listMode]);

  const showDoubleClickEditHint = useMemo(
    () => filteredContracts.some(contractCanReceiveDashboardEdit),
    [filteredContracts]
  );

  const hasMoreFromApi = generatedLastKey != null || uploadsLastKey != null;

  const showLoadMore =
    listMode === 'browse' &&
    sortedContracts.length > 0 &&
    (hasMoreFromApi || visibleCount < sortedContracts.length);

  const hasActiveFilters =
    Boolean(filters.search?.trim()) ||
    filters.status !== 'all' ||
    filters.type !== 'all';

  const handleAdvancedSearch = useCallback(
    async (criteria) => {
      setIsAdvancedSearching(true);
      try {
        const result = await contractService.searchAllDocuments(criteria);
        if (!result?.success) {
          showToast(
            'error',
            result?.error ?? 'Erro na busca avançada.'
          );
          return;
        }
        const mapped = (result.data.items || []).map(mapSearchItemToContract);
        setSearchResults(mapped);
        setSearchTruncated(Boolean(result.data.truncated));
        setListMode('search');
        setAdvancedSearchOpen(false);
        setVisibleCount(LIST_PAGE_SIZE);
        if (mapped.length === 0) {
          showToast('info', 'Nenhum documento encontrado para os filtros informados.');
        } else if (result.data.truncated) {
          showToast(
            'info',
            `Mostrando os primeiros ${DOCUMENT_SEARCH_MAX_RESULTS} resultados. Refine os filtros para ver mais.`
          );
        }
      } catch (error) {
        console.error('handleAdvancedSearch', error);
        showToast('error', error);
      } finally {
        setIsAdvancedSearching(false);
      }
    },
    []
  );

  const handleExitAdvancedSearch = useCallback(() => {
    setListMode('browse');
    setSearchResults([]);
    setSearchTruncated(false);
    setVisibleCount(LIST_PAGE_SIZE);
  }, []);

  const loadDocuments = async (loadMore = false) => {
    try {
      loadMore ? setIsLoadingMore(true) : setIsLoading(true);

      const pageOpts = { pageSize: LIST_PAGE_SIZE };

      const fetchGenerated = loadMore
        ? generatedLastKey
          ? contractService.listDocuments({
              lastEvaluatedKey: generatedLastKey,
              ...pageOpts,
            })
          : Promise.resolve({
              success: true,
              data: { items: [], lastEvaluatedKey: null },
            })
        : contractService.listDocuments({ ...pageOpts });

      const fetchUploads = loadMore
        ? uploadsLastKey
          ? listUploadedSignatureDocumentsPage(uploadsLastKey, pageOpts)
          : Promise.resolve({
              success: true,
              data: { items: [], lastEvaluatedKey: null },
            })
        : listUploadedSignatureDocumentsPage(null, pageOpts);

      const [listResult, uploadedResult] = await Promise.all([
        fetchGenerated,
        fetchUploads,
      ]);

      if (!listResult?.success) {
        showToast(
          'error',
          listResult?.error ?? 'Erro ao carregar documentos.'
        );
        return;
      }

      const mappedGenerated = (listResult.data.items || []).map(mapDocumentToContract);
      const mappedUploaded = uploadedResult?.success
        ? (uploadedResult.data?.items || []).map(mapUploadedDocumentToContract)
        : [];

      if (!uploadedResult?.success && !loadMore) {
        showToast(
          'error',
          uploadedResult?.error ?? 'Erro ao carregar documentos enviados.'
        );
      }

      if (loadMore) {
        setContracts((prev) =>
          mergeContractsByCreatedAt(prev, mappedGenerated, mappedUploaded)
        );
      } else {
        setContracts(mergeContractsByCreatedAt(mappedGenerated, mappedUploaded));
        setVisibleCount(LIST_PAGE_SIZE);
      }

      setGeneratedLastKey(listResult.data.lastEvaluatedKey || null);
      setUploadsLastKey(uploadedResult?.data?.lastEvaluatedKey || null);
    } catch (error) {
      console.error('Error loading documents:', error);
      showToast('error', error);
    } finally {
      loadMore ? setIsLoadingMore(false) : setIsLoading(false);
    }
  };

  /* =======================
     FILTERING
  ======================= */

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      type: 'all',
    });
  };

  const handleSortColumn = useCallback((key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const handleLoadMoreContracts = async () => {
    if (generatedLastKey || uploadsLastKey) {
      await loadDocuments(true);
    }
    setVisibleCount((c) => c + LIST_PAGE_SIZE);
  };

  useEffect(() => {
    if (listMode === 'browse') {
      setVisibleCount(LIST_PAGE_SIZE);
    }
  }, [listTab, filters.search, filters.status, filters.type, listMode]);

  const resetDocumentPagination = useCallback(() => {
    setGeneratedLastKey(null);
    setUploadsLastKey(null);
  }, []);

  const refreshDocumentList = useCallback(async () => {
    if (listMode === 'search') {
      handleExitAdvancedSearch();
    }
    resetDocumentPagination();
    await loadDocuments(false);
    await loadUserMetrics();
  }, [listMode, handleExitAdvancedSearch, resetDocumentPagination]);

  /* =======================
     ACTIONS
  ======================= */

  const handleDownload = async (contract) => {
    try {
      setIsDownloading(true);

      const result = contract.is_manual_signature
        ? await downloadUploadedSignaturePdf(contract.job_id)
        : await contractService.downloadDocument(contract);

      if (!result?.success) {
        showToast(
          'error',
          result?.error ?? 'Erro ao baixar documento.'
        );
        return;
      }

      const { blob, fileName } = result.data;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('success', 'Download iniciado.');
    } catch (err) {
      console.error('handleDownload', err);
      showToast('error', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewPdf = useCallback((contract) => {
    if (!contract) return;
    if (contract.is_manual_signature) {
      if (!contract.job_id) {
        showToast('error', 'Não foi possível abrir o PDF.');
        return;
      }
      const uploadViewable =
        contract.status === 'PDF_UPLOADED' ||
        contract.status === 'PDF_GENERATED' ||
        contract.status === 'PENDING_SIGNATURE' ||
        contract.status === 'SIGNED';
      if (!uploadViewable) {
        showToast('error', 'PDF ainda não está disponível para visualização.');
        return;
      }
    } else if (contract.status !== 'PDF_GENERATED') {
      showToast('error', 'Visualização disponível quando o PDF estiver gerado.');
      return;
    } else if (!contract.s3Key) {
      showToast('error', 'Não foi possível abrir o PDF.');
      return;
    }
    setPdfViewerContract(contract);
    setPdfViewerOpen(true);
  }, []);

  const handleClosePdfViewer = useCallback(() => {
    setPdfViewerOpen(false);
    setPdfViewerContract(null);
  }, []);

  const pdfViewerLoadPdf = useCallback(async () => {
    const contract = pdfViewerContract;
    if (!contract) {
      return { success: false, error: 'Documento inválido.' };
    }
    if (contract.is_manual_signature) {
      return downloadUploadedSignaturePdf(contract.job_id);
    }
    return contractService.downloadDocument({
      s3Key: contract.s3Key,
      name: contract.name,
    });
  }, [pdfViewerContract]);

  const handleEditContract = (contract) => {
    if (contract?.is_manual_signature) {
      showToast('error', 'Documentos enviados por upload não podem ser editados aqui.');
      return;
    }
    if (!contract?.job_id) {
      showToast('error', 'Não foi possível abrir o editor: documento sem job_id.');
      return;
    }
    if (contract.is_manual) {
      navigate(
        `/write-contract?job_id=${encodeURIComponent(String(contract.job_id))}`
      );
      return;
    }
    navigate(
      `/contract-generator?job_id=${encodeURIComponent(String(contract.job_id))}`
    );
  };

  const handleRequestGeneratePdf = useCallback((contract) => {
    if (!contract?.job_id) {
      showToast('error', 'Não foi possível gerar PDF: documento sem job_id.');
      return;
    }
    if (contract.is_manual) {
      showToast('error', 'Documentos manuais não podem ser gerados por este fluxo.');
      return;
    }
    if (
      contract.status !== 'DRAFT' &&
      contract.status !== 'PDF_GENERATED'
    ) {
      showToast('error', 'Só é possível gerar PDF para rascunhos ou PDFs já gerados.');
      return;
    }
    setPdfConfirmContract(contract);
  }, []);

  const handleCloseGeneratePdfConfirm = useCallback(() => {
    if (generatingPdfJobId || isPdfProcessing) return;
    setPdfConfirmContract(null);
  }, [generatingPdfJobId, isPdfProcessing]);

  const handleConfirmGeneratePdf = useCallback(async () => {
    const contract = pdfConfirmContract;
    if (!contract?.job_id || generatingPdfJobId || isPdfProcessing) return;

    setPdfConfirmContract(null);
    setGeneratingPdfJobId(String(contract.job_id));

    try {
      const docResult = await contractService.getDocumentByJobId(contract.job_id);
      if (!docResult?.success) {
        showToast(
          'error',
          resolveApiErrorMessage(docResult?.error) || 'Erro ao carregar documento.'
        );
        return;
      }

      let payload;
      try {
        payload = await buildStoredDocumentContractPayload(docResult.data);
      } catch (err) {
        showToast('error', err?.message || 'Não foi possível montar o documento.');
        return;
      }

      const result = await contractService.generateContract(payload);
      if (!result?.success) {
        showToast(
          'error',
          resolveApiErrorMessage(result?.error) || 'Erro ao gerar documento.'
        );
        return;
      }

      setIsPdfProcessing(true);
      trackDocumentJob({
        jobId: contract.job_id,
        title: contract.name || contract.title || 'Documento',
        kind: 'pdf_generation',
      });
      showToast(
        'success',
        'Documento em processamento. Acompanhe o status abaixo.'
      );

      pollDocumentJobStatus(String(contract.job_id))
        .finally(async () => {
          resetDocumentPagination();
          await loadDocuments(false);
          await loadUserMetrics();
          setIsPdfProcessing(false);
        });
    } catch (err) {
      console.error('handleConfirmGeneratePdf', err);
      showToast('error', 'Erro ao gerar documento.');
    } finally {
      setGeneratingPdfJobId(null);
    }
  }, [
    pdfConfirmContract,
    generatingPdfJobId,
    isPdfProcessing,
    loadDocuments,
    loadUserMetrics,
    resetDocumentPagination,
  ]);

  const requestDelete = (contract) => {
    setDocumentToDelete(contract);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      setIsDeleting(true);

      const result = documentToDelete.is_manual_signature
        ? await deleteUploadedSignatureDocument(documentToDelete.job_id)
        : await contractService.deleteDocument(documentToDelete.s3Key);

      if (!result?.success) {
        showToast(
          'error',
          result?.error ?? 'Erro ao excluir documento.'
        );
        return;
      }

      resetDocumentPagination();
      await loadDocuments(false);
      await loadUserMetrics();

      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
      showToast('success', 'Documento excluído com sucesso.');
    } catch (err) {
      console.error('confirmDelete', err);
      showToast('error', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, []);

  const handleSendToSignature = (contract) => {
    const jobId = contract?.job_id;
    if (!jobId) {
      showToast('error', 'Não foi possível identificar o documento para assinatura.');
      return;
    }

    const canOpenEditor = contract.is_manual_signature
      ? contract.status === 'PDF_UPLOADED' ||
        contract.status === 'PDF_GENERATED' ||
        contract.status === 'PENDING_SIGNATURE'
      : contract.status === 'PDF_GENERATED' ||
        contract.status === 'PENDING_SIGNATURE';
    if (!canOpenEditor) {
      showToast(
        'error',
        contract.is_manual_signature
          ? 'Só é possível assinar quando o PDF está pronto para envio.'
          : 'Só é possível editar campos quando o PDF já foi gerado.'
      );
      return;
    }

    setSignatureChecklistContract(contract);
    setSignatureChecklistOpen(true);
  };

  const handleCloseSignatureChecklist = useCallback(() => {
    setSignatureChecklistOpen(false);
    setSignatureChecklistContract(null);
  }, []);

  const handleContinueSignatureChecklist = useCallback(() => {
    const contract = signatureChecklistContract;
    setSignatureChecklistOpen(false);
    if (!contract) return;
    setSignatureEditorContract(contract);
    setSignatureEditorOpen(true);
    setSignatureChecklistContract(null);
  }, [signatureChecklistContract]);

  const handleCloseSignatureEditor = useCallback(() => {
    setSignatureEditorOpen(false);
    setSignatureEditorContract(null);
  }, []);

  const handleSignatureEditorSent = useCallback(async () => {
    setSignatureEditorOpen(false);
    setSignatureEditorContract(null);
    resetDocumentPagination();
    await loadDocuments(false);
    await loadUserMetrics();
    showToast('success', 'Documento enviado para assinatura com sucesso.');
  }, [loadDocuments, loadUserMetrics, resetDocumentPagination]);

  const handleDuplicateContract = useCallback(async (contract) => {
    if (!contract?.job_id || !contract?.is_manual) {
      showToast('error', 'Erro ao duplicar documento.');
      return;
    }

    setIsDuplicating(true);
    try {
      const result = await duplicateDocument(String(contract.job_id));
      if (result.ok) {
        showToast('success', 'Documento duplicado com sucesso');
        resetDocumentPagination();
        await loadDocuments(false);
        await loadUserMetrics();
        return;
      }
      showToast(
        'error',
        result.message ?? 'Erro ao duplicar documento.'
      );
    } catch (err) {
      console.error('handleDuplicateContract', err);
      showToast('error', err);
    } finally {
      setIsDuplicating(false);
    }
  }, [resetDocumentPagination, loadDocuments, loadUserMetrics]);

  const handleCloseCancelSignatureConfirm = useCallback(() => {
    setSignatureCancelConfirmOpen(false);
    setPendingCancelSignatureContract(null);
  }, []);

  const handleRequestCancelSignature = useCallback((contract) => {
    const jobId = contract?.job_id;
    if (!jobId) {
      setSignatureCancelFeedback({
        variant: 'error',
        message: 'Não foi possível identificar o documento.',
      });
      return;
    }
    setPendingCancelSignatureContract(contract);
    setSignatureCancelConfirmOpen(true);
  }, []);

  const handleViewSignatureStatus = useCallback((contract) => {
    if (!contract?.job_id) {
      showToast('error', 'Não foi possível identificar o documento.');
      return;
    }
    setSignatureStatusDocument(contract);
    setSignatureStatusOpen(true);
  }, []);

  const handleConfirmCancelSignature = useCallback(async () => {
    const contract = pendingCancelSignatureContract;
    setSignatureCancelConfirmOpen(false);
    setPendingCancelSignatureContract(null);

    const jobId = contract?.job_id;
    if (!jobId) return;

    setIsCancelingSignature(true);
    try {
      const result = await cancelSignatureJob(String(jobId));
      await refreshDocumentList();
      if (result.ok) {
        setSignatureCancelFeedback({
          variant: 'success',
          message: 'Envio para assinatura cancelado com sucesso!',
        });
        return;
      }
      setSignatureCancelFeedback({
        variant: 'error',
        message: result.messagePt || 'Não foi possível cancelar o envio para assinatura.',
      });
    } catch (err) {
      console.error('handleConfirmCancelSignature', err);
      setSignatureCancelFeedback({
        variant: 'error',
        message: formatApiErrorMessage(
          err,
          'Erro ao cancelar envio para assinatura.'
        ),
      });
    } finally {
      setIsCancelingSignature(false);
    }
  }, [pendingCancelSignatureContract, refreshDocumentList]);

  /* =======================
     RENDER
  ======================= */

  return (
    <>
      <Helmet>
        <title>Documentos - neo-modelos</title>
      </Helmet>

      <div className={PAGE_CONTENT_CLASS}>
            <ContractMetrics user={user} />

            <LicenseLimitBanner user={user} />

            <div data-tour="tour-documents-area">
            <ContractFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              onRefresh={refreshDocumentList}
              onOpenAdvancedSearch={() => setAdvancedSearchOpen(true)}
            />

            {listMode === 'search' ? (
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3 text-sm">
                <p className="text-muted-foreground flex-1">
                  Resultados da busca avançada: {searchResults.length} documento(s)
                  {searchTruncated
                    ? ` (limite de ${DOCUMENT_SEARCH_MAX_RESULTS}; refine os filtros)`
                    : ''}
                  .
                </p>
                <Button type="button" variant="outline" size="sm" onClick={handleExitAdvancedSearch}>
                  Voltar à lista recente
                </Button>
              </div>
            ) : null}

            <DocumentActivityStrip
              onJobFinished={() => {
                setGeneratedLastKey(null);
                setUploadsLastKey(null);
                loadDocuments(false);
              }}
            />

            <div
              className="flex gap-0 border-b border-border mb-6 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-none"
              role="tablist"
              aria-label="Tipo de documento"
            >
              <button
                type="button"
                role="tab"
                aria-selected={listTab === 'todos'}
                onClick={() => setListTab('todos')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
                  listTab === 'todos'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Todos os Documentos
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listTab === 'modelos'}
                onClick={() => setListTab('modelos')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
                  listTab === 'modelos'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Modelos (Sistema)
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listTab === 'manuais'}
                onClick={() => setListTab('manuais')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
                  listTab === 'manuais'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Manuais
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listTab === 'templates'}
                onClick={() => setListTab('templates')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
                  listTab === 'templates'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Meus Modelos
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listTab === 'uploads'}
                onClick={() => setListTab('uploads')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
                  listTab === 'uploads'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Documentos Upload
              </button>
            </div>

            {isLoading ? (
              <DocumentListSkeleton />
            ) : filteredContracts.length === 0 ? (
              <EmptyState
                listTab={listTab}
                hasActiveFilters={hasActiveFilters}
              />
            ) : (
              <>
                {showDoubleClickEditHint && !isUploadsTab ? (
                  <p className="mb-4 text-xs text-muted-foreground">
                    Clique duas vezes para editar os documentos
                  </p>
                ) : null}
                <div className="hidden md:block">
                  {isTemplatesTab ? (
                    <TemplateDocumentTable
                      contracts={visibleContracts}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSortColumn={handleSortColumn}
                      onDownload={handleDownload}
                      onEdit={handleEditContract}
                      onDelete={requestDelete}
                      onSendToSignature={handleSendToSignature}
                      onCancelSignature={handleRequestCancelSignature}
                      onViewSignatureStatus={handleViewSignatureStatus}
                      onGeneratePdf={handleRequestGeneratePdf}
                      onViewPdf={handleViewPdf}
                      generatingPdfJobId={generatingPdfJobId}
                    />
                  ) : (
                    <ContractTable
                      contracts={visibleContracts}
                      mixed={isAllDocumentsTab}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSortColumn={handleSortColumn}
                      onDownload={handleDownload}
                      onEdit={handleEditContract}
                      onDelete={requestDelete}
                      onSendToSignature={handleSendToSignature}
                      onDuplicateContract={handleDuplicateContract}
                      onCancelSignature={handleRequestCancelSignature}
                      onViewSignatureStatus={handleViewSignatureStatus}
                      onGeneratePdf={handleRequestGeneratePdf}
                      onViewPdf={handleViewPdf}
                      generatingPdfJobId={generatingPdfJobId}
                    />
                  )}
                </div>

                <div className="md:hidden space-y-4">
                  {visibleContracts.map((contract) => {
                    const isTemplateDoc =
                      isTemplatesTab ||
                      (isAllDocumentsTab &&
                        contract.fillingType === 'custom_template' &&
                        !contract.is_manual_signature);

                    if (isTemplateDoc) {
                      return (
                        <TemplateDocumentCardMobile
                          key={contract.id}
                          contract={contract}
                          onDownload={handleDownload}
                          onEdit={handleEditContract}
                          onDelete={requestDelete}
                          onSendToSignature={handleSendToSignature}
                          onCancelSignature={handleRequestCancelSignature}
                          onViewSignatureStatus={handleViewSignatureStatus}
                          onGeneratePdf={handleRequestGeneratePdf}
                          onViewPdf={handleViewPdf}
                          isGeneratingPdf={
                            generatingPdfJobId != null &&
                            String(contract.job_id) === String(generatingPdfJobId)
                          }
                        />
                      );
                    }

                    return (
                      <ContractCardMobile
                        key={contract.id}
                        contract={contract}
                        onDownload={handleDownload}
                        onEdit={handleEditContract}
                        onDelete={requestDelete}
                        onSendToSignature={handleSendToSignature}
                        onDuplicateContract={handleDuplicateContract}
                        onCancelSignature={handleRequestCancelSignature}
                        onViewSignatureStatus={handleViewSignatureStatus}
                        onGeneratePdf={handleRequestGeneratePdf}
                        onViewPdf={handleViewPdf}
                        isGeneratingPdf={
                          generatingPdfJobId != null &&
                          String(contract.job_id) === String(generatingPdfJobId)
                        }
                      />
                    );
                  })}
                </div>

                {showLoadMore ? (
                  <div className="flex justify-center mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleLoadMoreContracts}
                      disabled={isLoadingMore}
                      className="min-w-[140px]"
                    >
                      {isLoadingMore ? 'Carregando…' : 'Ver Mais'}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
            </div>
      </div>

      <DownloadDocumentOverlay visible={isDownloading} />

      <DownloadDocumentOverlay
        visible={isDuplicating}
        message="Duplicando documento"
      />

      <DownloadDocumentOverlay
        visible={isCancelingSignature}
        message="Cancelando envio para assinatura"
      />

      <DownloadDocumentOverlay
        visible={isPdfProcessing}
        message="Seu documento está sendo gerado..."
      />

      <GeneratePdfConfirmDialog
        open={Boolean(pdfConfirmContract)}
        documentTitle={pdfConfirmContract?.name ?? ''}
        onCancel={handleCloseGeneratePdfConfirm}
        onConfirm={handleConfirmGeneratePdf}
      />

      <SignatureSendChecklistModal
        open={signatureChecklistOpen}
        contract={signatureChecklistContract}
        onClose={handleCloseSignatureChecklist}
        onContinue={handleContinueSignatureChecklist}
      />

      <SignatureCancelFeedbackDialog
        open={Boolean(signatureCancelFeedback)}
        message={signatureCancelFeedback?.message ?? ''}
        variant={signatureCancelFeedback?.variant ?? 'error'}
        onOk={() => setSignatureCancelFeedback(null)}
      />

      <SignatureCancelConfirmDialog
        open={signatureCancelConfirmOpen}
        documentTitle={pendingCancelSignatureContract?.name ?? ''}
        onCancel={handleCloseCancelSignatureConfirm}
        onConfirm={handleConfirmCancelSignature}
      />

      <SignatureStatusModal
        open={signatureStatusOpen}
        document={signatureStatusDocument}
        onClose={() => {
          setSignatureStatusOpen(false);
          setSignatureStatusDocument(null);
        }}
      />

      <ContractDeleteDialog
        open={isDeleteDialogOpen}
        isDeleting={isDeleting}
        onCancel={handleCloseDeleteDialog}
        onConfirm={confirmDelete}
      />

      {signatureEditorOpen ? (
        <Suspense
          fallback={
            <DownloadDocumentOverlay
              visible
              message="Carregando editor de assinatura"
            />
          }
        >
          <SignatureFieldEditorModal
            open={signatureEditorOpen}
            jobId={signatureEditorContract?.job_id ?? null}
            documentTitle={signatureEditorContract?.name ?? ''}
            documentStatus={signatureEditorContract?.status ?? ''}
            onClose={handleCloseSignatureEditor}
            onSentToSignature={handleSignatureEditorSent}
          />
        </Suspense>
      ) : null}

      {pdfViewerOpen ? (
        <Suspense
          fallback={
            <DownloadDocumentOverlay visible message="Carregando visualizador de PDF" />
          }
        >
          <UploadedDocumentPdfViewerModal
            open={pdfViewerOpen}
            loadPdf={pdfViewerLoadPdf}
            documentTitle={pdfViewerContract?.name ?? ''}
            onClose={handleClosePdfViewer}
          />
        </Suspense>
      ) : null}

      <AdvancedDocumentSearchModal
        open={advancedSearchOpen}
        onClose={() => setAdvancedSearchOpen(false)}
        onSearch={handleAdvancedSearch}
        searching={isAdvancedSearching}
      />

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </>
  );
};

export default ContractDashboard;
